import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { Modal, Button, Icon } from 'antd'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import 'antd/dist/antd.css'

import './ImagePreview.scss'

const DESKTOP_STAGE_PADDING = 24
const MOBILE_STAGE_PADDING = 12
const DESKTOP_CONTROLS_OFFSET = 112
const MOBILE_CONTROLS_OFFSET = 84
const DESKTOP_INITIAL_FIT_RATIO = 0.72
const MOBILE_INITIAL_FIT_RATIO = 0.84

const normalizeImage = (img, fallbackAlt) => {
  if (typeof img === 'string') {
    return { src: img, alt: fallbackAlt || 'Preview' }
  }

  return {
    src: img.src,
    alt: img.alt || fallbackAlt || 'Preview'
  }
}

const getStageConfig = width => {
  const isMobile = width <= 768

  return {
    padding: isMobile ? MOBILE_STAGE_PADDING : DESKTOP_STAGE_PADDING,
    controlsOffset: isMobile
      ? MOBILE_CONTROLS_OFFSET
      : DESKTOP_CONTROLS_OFFSET,
    fitRatio: isMobile ? MOBILE_INITIAL_FIT_RATIO : DESKTOP_INITIAL_FIT_RATIO
  }
}

const getFittedImageSize = ({
  naturalWidth,
  naturalHeight,
  stageWidth,
  stageHeight
}) => {
  if (!naturalWidth || !naturalHeight || !stageWidth || !stageHeight) {
    return null
  }

  const { padding, controlsOffset, fitRatio } = getStageConfig(stageWidth)
  const usableWidth = Math.max(1, stageWidth - padding * 2)
  const usableHeight = Math.max(1, stageHeight - padding * 2 - controlsOffset)
  const targetWidth = usableWidth * fitRatio
  const targetHeight = usableHeight * fitRatio
  const scale = Math.min(
    targetWidth / naturalWidth,
    targetHeight / naturalHeight,
    1
  )

  return {
    width: Math.max(1, Math.round(naturalWidth * scale)),
    height: Math.max(1, Math.round(naturalHeight * scale))
  }
}

const ImagePreview = props => {
  const [visible, setVisible] = useState(false)
  const [rotation, setRotation] = useState(0)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFading, setIsFading] = useState(false)
  const [imageSize, setImageSize] = useState(null)
  const imageRef = useRef(null)
  const stageRef = useRef(null)
  const transformApiRef = useRef(null)
  const fadeTimerRef = useRef(null)
  const resizeObserverRef = useRef(null)
  const refitTimerRef = useRef(null)

  const imagesList = useMemo(() => {
    if (Array.isArray(props.images) && props.images.length > 0) {
      return props.images.map(img => normalizeImage(img, props.alt))
    }

    if (props.src) {
      return [{ src: props.src, alt: props.alt || 'Preview' }]
    }

    return []
  }, [props.images, props.src, props.alt])

  const isCarousel = imagesList.length > 1
  const activeImage = imagesList[currentIndex] || imagesList[0] || {}

  const clearRefitTimer = useCallback(() => {
    if (refitTimerRef.current) {
      clearTimeout(refitTimerRef.current)
      refitTimerRef.current = null
    }
  }, [])

  const fitImage = useCallback(() => {
    const img = imageRef.current
    const stage = stageRef.current

    if (!img || !stage) return

    const naturalWidth = img.naturalWidth
    const naturalHeight = img.naturalHeight
    const stageWidth = stage.clientWidth
    const stageHeight = stage.clientHeight
    const nextSize = getFittedImageSize({
      naturalWidth,
      naturalHeight,
      stageWidth,
      stageHeight
    })

    if (!nextSize) return

    setImageSize(prevSize => {
      if (
        prevSize &&
        prevSize.width === nextSize.width &&
        prevSize.height === nextSize.height
      ) {
        return prevSize
      }

      return nextSize
    })
  }, [])

  const resetPreview = useCallback(() => {
    const transformApi = transformApiRef.current

    setRotation(0)
    fitImage()

    if (transformApi && typeof transformApi.resetTransform === 'function') {
      transformApi.resetTransform(0)
    }

    if (transformApi && typeof transformApi.centerView === 'function') {
      transformApi.centerView(1, 0)
    }
  }, [fitImage])

  const scheduleRefit = useCallback(() => {
    clearRefitTimer()

    fitImage()

    // Modal/layout transitions can settle one tick later.
    refitTimerRef.current = setTimeout(() => {
      fitImage()
    }, 60)
  }, [clearRefitTimer, fitImage])

  const showModal = () => {
    const startIndex =
      typeof props.initialIndex === 'number'
        ? Math.max(0, Math.min(props.initialIndex, imagesList.length - 1))
        : 0

    setCurrentIndex(startIndex)
    setRotation(0)
    setIsFading(false)
    setImageSize(null)
    setVisible(true)
  }

  const handleCancel = () => {
    setVisible(false)
    setRotation(0)
    setIsFading(false)
    setImageSize(null)
    clearRefitTimer()

    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current)
    }
  }

  const navigateTo = useCallback(
    newIndex => {
      if (newIndex === currentIndex) return

      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current)
      }

      setIsFading(true)

      fadeTimerRef.current = setTimeout(() => {
        setCurrentIndex(newIndex)
        setRotation(0)
        setImageSize(null)

        fadeTimerRef.current = setTimeout(() => {
          setIsFading(false)
        }, 50)
      }, 200)
    },
    [currentIndex]
  )

  const goPrev = useCallback(() => {
    const newIndex =
      currentIndex === 0 ? imagesList.length - 1 : currentIndex - 1
    navigateTo(newIndex)
  }, [currentIndex, imagesList.length, navigateTo])

  const goNext = useCallback(() => {
    const newIndex =
      currentIndex === imagesList.length - 1 ? 0 : currentIndex + 1
    navigateTo(newIndex)
  }, [currentIndex, imagesList.length, navigateTo])

  const handleImageLoad = useCallback(() => {
    scheduleRefit()
  }, [scheduleRefit])

  useEffect(() => {
    if (!visible || !isCarousel) return undefined

    const handleKeyDown = e => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [visible, isCarousel, goPrev, goNext])

  useEffect(() => {
    if (!visible) return undefined

    const handleResize = () => {
      scheduleRefit()
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [scheduleRefit, visible])

  useEffect(() => {
    if (!visible || !stageRef.current || typeof ResizeObserver !== 'function') {
      return undefined
    }

    resizeObserverRef.current = new ResizeObserver(() => {
      scheduleRefit()
    })
    resizeObserverRef.current.observe(stageRef.current)

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
    }
  }, [scheduleRefit, visible])

  useEffect(() => {
    if (!visible) return undefined

    scheduleRefit()

    return () => {
      clearRefitTimer()
    }
  }, [activeImage.src, clearRefitTimer, scheduleRefit, visible])

  useEffect(() => {
    const transformApi = transformApiRef.current

    if (!visible || !imageSize || !transformApi) return undefined

    const frameId = requestAnimationFrame(() => {
      if (typeof transformApi.centerView === 'function') {
        transformApi.centerView(1, 0)
      }
    })

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [imageSize, visible])

  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current)
      }

      clearRefitTimer()
    }
  }, [clearRefitTimer])

  const imageFrameStyle = imageSize
    ? {
      width: `${imageSize.width}px`,
      height: `${imageSize.height}px`
    }
    : undefined

  return (
    <>
      <img
        key={props.src}
        alt={props.alt || 'Preview'}
        src={props.src}
        crossOrigin={props.crossorigin}
        onClick={showModal}
        onLoad={props.onLoad}
        onError={props.onError}
        className={props.className ? props.className : 'shl-image-preview'}
        style={{
          cursor: 'pointer',
          maxWidth: '100%',
          opacity: props.opacity ? props.opacity : 1,
          ...props.style
        }}
      />

      <Modal
        visible={visible}
        onCancel={handleCancel}
        footer={null}
        width="100vw"
        style={{
          maxWidth: '100vw',
          margin: 0,
          top: 0,
          padding: 0
        }}
        bodyStyle={{
          height: '100dvh',
          padding: 0
        }}
        wrapClassName="shl-image-preview-modal"
        destroyOnClose
      >
        <div className="image-preview-container">
          {isCarousel && (
            <>
              <button
                className="carousel-nav carousel-nav-prev"
                onClick={e => {
                  e.stopPropagation()
                  goPrev()
                }}
                aria-label="Previous image"
              >
                <Icon type="left" />
              </button>
              <button
                className="carousel-nav carousel-nav-next"
                onClick={e => {
                  e.stopPropagation()
                  goNext()
                }}
                aria-label="Next image"
              >
                <Icon type="right" />
              </button>
            </>
          )}

          {isCarousel && (
            <div className="carousel-info">
              {activeImage.alt && (
                <span className="carousel-title">{activeImage.alt}</span>
              )}
              <span className="carousel-counter">
                {currentIndex + 1} / {imagesList.length}
              </span>
            </div>
          )}

          <TransformWrapper
            initialScale={1}
            minScale={0.2}
            maxScale={10}
            centerOnInit
            centerZoomedOut
            limitToBounds={false}
            wheel={{ step: 0.12 }}
            pinch={{ step: 0.12 }}
            doubleClick={{ disabled: true }}
            alignmentAnimation={{ disabled: true }}
            panning={{ velocityDisabled: true }}
          >
            {({ zoomIn, zoomOut, resetTransform, centerView }) => {
              transformApiRef.current = { resetTransform, centerView }

              return (
                <>
                  <div className="shl-tools">
                    <Button onClick={() => zoomIn()}>
                      <Icon type="zoom-in" />
                    </Button>
                    <Button onClick={() => zoomOut()}>
                      <Icon type="zoom-out" />
                    </Button>
                    <Button onClick={() => setRotation(prev => prev + 90)}>
                      <Icon type="redo" />
                    </Button>
                    <Button onClick={() => setRotation(prev => prev - 90)}>
                      <Icon type="undo" />
                    </Button>
                    <Button className="btn-reset" onClick={resetPreview}>
                      Reset
                    </Button>
                  </div>

                  <div ref={stageRef} className="image-preview-stage">
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '100%'
                      }}
                      contentStyle={{
                        width: imageSize ? `${imageSize.width}px` : 'auto',
                        height: imageSize ? `${imageSize.height}px` : 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <div
                        className={`image-preview-frame ${isFading ? 'is-fading' : ''}`}
                        style={imageFrameStyle}
                      >
                        <img
                          ref={imageRef}
                          alt={activeImage.alt || 'Preview'}
                          src={activeImage.src}
                          crossOrigin={props.crossorigin}
                          onLoad={handleImageLoad}
                          className="image-preview-full"
                          style={{
                            transform: `rotate(${rotation}deg)`
                          }}
                        />
                      </div>
                    </TransformComponent>
                  </div>
                </>
              )
            }}
          </TransformWrapper>
        </div>
      </Modal>
    </>
  )
}

export default ImagePreview
